// matchmaking.ts — the personal board's opponent rules, as the form holds them
// and as the wire carries them.
//
// The draft below is not the DTO, and the difference is the usual one: every
// number here is a STRING because it is bound straight to an input, and «no
// quota» is an empty box rather than an absent key. Converting in one place is
// what keeps the editor from having to know how the column spells a rule the
// operator left blank.
//
// The vocabulary is CLOSED — six layers, three preferences — and it is closed
// on the server too (validateMatchmakingConfig). What lives here is the copy
// that says what each one does; what lives there is the refusal.

import type {
  MatchmakingConfig,
  MatchmakingLayer,
  MatchmakingLayerKind,
  MatchmakingPrefer,
} from "./types";

/** One row of the layer editor. */
export interface LayerDraft {
  /** Stable identity across reorders — the array index is not one. */
  uid: string;
  kind: MatchmakingLayerKind;
  /** Empty means «the rest»: everything still missing from the list. */
  quota: string;
  prefer: MatchmakingPrefer | "";
  /** similar_pace only. */
  band_pct: string;
  /** similar_pace only: "0" never widens. */
  widen_steps: string;
}

/** The whole card, as the form holds it. */
export interface MatchmakingDraft {
  gate_on: boolean;
  gate_key: string;
  gate_min: string;
  freshness_days: string;
  /** Empty means «treat them as a newcomer» — the pace layer yields nothing for
     a player with no previous cycle and the remainder goes down. */
  fallback_stat_key: string;
  layers: LayerDraft[];
}

let layerUidSeq = 0;

function uid(): string {
  return `mm-${++layerUidSeq}`;
}

/** What each layer is called on screen, and what it actually selects. The two
   sentences are the whole of the add-menu and of every row's label. */
export const LAYER_META: Record<
  MatchmakingLayerKind,
  { title: string; desc: string }
> = {
  friends: { title: "Friends", desc: "Mutual friends of the player." },
  guild: { title: "Guild mates", desc: "Members of the player's guild." },
  similar_pace: {
    title: "Similar pace",
    desc: "Neighbours by previous-cycle score.",
  },
  recent_active: { title: "Recently active", desc: "Any live candidate." },
  same_country: {
    title: "Same country",
    desc: "Only players from the list owner's country.",
  },
  bots: { title: "Bots", desc: "Simulation fills the rest." },
};

/** The order the add-menu lists them in — the order the default set is built
   in, so a menu and a fresh board read the same way down the page. */
export const LAYER_KINDS: MatchmakingLayerKind[] = [
  "friends",
  "guild",
  "similar_pace",
  "recent_active",
  "same_country",
  "bots",
];

export const PREFER_LABEL: Record<MatchmakingPrefer, string> = {
  fresher_last_seen: "fresher last seen",
  same_timezone: "same timezone",
  random: "random",
};

/**
 * A layer that can always produce another candidate, and therefore the only
 * kind a list may END on.
 *
 * The rule is the server's (isFillerLayer), repeated here so the editor can say
 * so under the list instead of letting the operator find out from a 400:
 * `recent_active` runs out only when the project does, and `bots` never runs
 * out at all. A list whose last layer is `friends` is a list that stays nine
 * long for everybody with two friends — the exact failure this board type
 * exists to remove.
 */
export function isFillerLayer(kind: MatchmakingLayerKind): boolean {
  return kind === "recent_active" || kind === "bots";
}

/**
 * What a board switched from rooms to lists starts with.
 *
 * It is the set the operator would otherwise have to assemble by hand to get
 * back what the room was giving them — people to play against — with the two
 * layers that make a personal list worth having at the top. `bots` carries no
 * cap on purpose: a capped filler can leave a list short, and a board's FIRST
 * cycle is the worst possible moment to discover that. Adding the cap is one
 * box; discovering an incomplete list is a week.
 */
export function defaultMatchmakingDraft(): MatchmakingDraft {
  return {
    gate_on: false,
    gate_key: "",
    gate_min: "",
    freshness_days: "14",
    fallback_stat_key: "",
    layers: [
      { uid: uid(), kind: "friends", quota: "3", prefer: "fresher_last_seen", band_pct: "", widen_steps: "" },
      { uid: uid(), kind: "guild", quota: "2", prefer: "fresher_last_seen", band_pct: "", widen_steps: "" },
      { uid: uid(), kind: "similar_pace", quota: "", prefer: "same_timezone", band_pct: "30", widen_steps: "3" },
      { uid: uid(), kind: "recent_active", quota: "", prefer: "same_timezone", band_pct: "", widen_steps: "" },
      { uid: uid(), kind: "bots", quota: "", prefer: "", band_pct: "", widen_steps: "" },
    ],
  };
}

/** A row for a layer just picked from the menu, with the defaults that kind
   needs to be legal on its own. */
export function newLayerDraft(kind: MatchmakingLayerKind): LayerDraft {
  return {
    uid: uid(),
    kind,
    quota: "",
    prefer: kind === "bots" ? "" : "fresher_last_seen",
    band_pct: kind === "similar_pace" ? "30" : "",
    widen_steps: kind === "similar_pace" ? "3" : "",
  };
}

function numText(n: number | null | undefined): string {
  return n === undefined || n === null ? "" : String(n);
}

/** The stored config as the editor holds it. */
export function draftFromConfig(cfg: MatchmakingConfig | undefined): MatchmakingDraft {
  if (!cfg) return defaultMatchmakingDraft();
  return {
    gate_on: !!cfg.entry_gate,
    gate_key: cfg.entry_gate?.key ?? "",
    gate_min: numText(cfg.entry_gate?.min),
    freshness_days: numText(cfg.freshness_days),
    fallback_stat_key: cfg.strength?.fallback_stat_key ?? "",
    layers: (cfg.layers ?? []).map((l) => ({
      uid: uid(),
      kind: l.kind,
      quota: numText(l.quota),
      prefer: l.prefer ?? "",
      band_pct: numText(l.band_pct),
      widen_steps: numText(l.widen_steps),
    })),
  };
}

/**
 * The editor's draft as the column holds it.
 *
 * An empty box is an ABSENT key, never a zero: `quota` absent is «the rest»,
 * `fallback_stat_key` absent is «treat them as a newcomer», and a 0 in either
 * place would be a rule nobody wrote. A half-typed number is left to
 * `validateMatchmaking`, which blocks the submit before this runs.
 */
export function configFromDraft(draft: MatchmakingDraft): MatchmakingConfig {
  const cfg: MatchmakingConfig = {
    freshness_days: Number(draft.freshness_days),
    strength: { primary: "previous_cycle_score" },
    layers: draft.layers.map((l) => {
      const layer: MatchmakingLayer = { kind: l.kind };
      if (l.quota.trim() !== "") layer.quota = Number(l.quota);
      if (l.prefer) layer.prefer = l.prefer;
      if (l.kind === "similar_pace") {
        layer.band_pct = Number(l.band_pct);
        if (l.widen_steps.trim() !== "") layer.widen_steps = Number(l.widen_steps);
      }
      return layer;
    }),
  };
  if (draft.fallback_stat_key.trim() !== "") {
    cfg.strength.fallback_stat_key = draft.fallback_stat_key.trim();
  }
  if (draft.gate_on) {
    cfg.entry_gate = { key: draft.gate_key.trim(), min: Number(draft.gate_min) };
  }
  return cfg;
}

/** Per-row messages, keyed by the row's uid. */
export type LayerErrors = Record<string, string>;

export interface MatchmakingErrors {
  gate?: string;
  freshness_days?: string;
  layers?: string;
  rows?: LayerErrors;
}

/**
 * The same rules validateMatchmakingConfig applies on the server, answered
 * under the field they are about rather than as a banner after a save.
 *
 * `rosterSize` is the ceiling every quota is judged against: a layer that may
 * take the whole list leaves nothing for the filler underneath it, so the
 * highest legal quota is one below the length — the owner's own slot is not
 * anybody's to fill.
 */
export function validateMatchmaking(
  draft: MatchmakingDraft,
  rosterSize: number,
): MatchmakingErrors {
  const errors: MatchmakingErrors = {};
  const rows: LayerErrors = {};

  if (draft.gate_on) {
    if (draft.gate_key.trim() === "") {
      errors.gate = "Pick the stat the threshold is measured on.";
      // The empty box is the DEFAULT state of a gate just switched on, and
      // Number("") is 0 — so a check for «is this a number» passes it and
      // writes `min: 0`, a threshold every player in the project clears. The
      // operator sees a gate that is on and turns nobody away.
    } else if (draft.gate_min.trim() === "") {
      errors.gate = "Set the threshold this stat has to reach.";
      // Integer, like every other number in this card: entry_gate.min is an
      // int64 on the server, so 47.5 comes back as a decode error naming a Go
      // type rather than as a message about this field.
    } else if (!Number.isInteger(Number(draft.gate_min))) {
      errors.gate = "The threshold must be a whole number.";
    }
  }

  const fresh = Number(draft.freshness_days);
  if (!Number.isInteger(fresh) || fresh < 1) {
    errors.freshness_days = "Candidate freshness must be an integer ≥ 1 day.";
  }

  if (draft.layers.length === 0) {
    errors.layers = "A list needs at least one selection layer.";
  } else if (!isFillerLayer(draft.layers[draft.layers.length - 1].kind)) {
    errors.layers =
      "The last layer must be a filler (recent_active or bots) — otherwise a list can stay incomplete.";
  }

  const seen = new Map<MatchmakingLayerKind, number>();
  for (const l of draft.layers) {
    seen.set(l.kind, (seen.get(l.kind) ?? 0) + 1);
  }
  const maxQuota = Number.isInteger(rosterSize) && rosterSize > 1 ? rosterSize - 1 : 0;
  for (const l of draft.layers) {
    if ((seen.get(l.kind) ?? 0) > 1) {
      rows[l.uid] = `${LAYER_META[l.kind].title} appears twice — one rule per kind.`;
      continue;
    }
    if (l.quota.trim() !== "") {
      const q = Number(l.quota);
      if (!Number.isInteger(q) || q < 1 || q > maxQuota) {
        rows[l.uid] = maxQuota
          ? `Quota must be an integer between 1 and ${maxQuota}.`
          : "Set a list size before capping a layer.";
        continue;
      }
    }
    if (l.kind === "similar_pace") {
      const band = Number(l.band_pct);
      if (!Number.isInteger(band) || band < 1 || band > 100) {
        rows[l.uid] = "Band must be an integer between 1 and 100 %.";
      }
    }
  }
  if (Object.keys(rows).length > 0) errors.rows = rows;
  return errors;
}

/** Whether anything above blocks the save. */
export function hasMatchmakingErrors(e: MatchmakingErrors): boolean {
  return !!(e.gate || e.freshness_days || e.layers || e.rows);
}
